/**
 * Velox Privacy Notice content (Velox Sàrl, Luxembourg).
 * Source: legal text provided by Velox — last updated 31.08.2026.
 * Kept out of the i18n locale files because of its length.
 */

export interface PrivacySection {
  title: string;
  body: string;
}

export interface PrivacyNoticeContent {
  title: string;
  subtitle: string;
  lastUpdated: string;
  back: string;
  sections: PrivacySection[];
}

const en: PrivacyNoticeContent = {
  title: 'Privacy Notice',
  subtitle: 'Applicable to the Website, the Carrier Portal, the App, and related Services',
  lastUpdated: 'Last updated: 31.08.2026',
  back: 'Back to home',
  sections: [
    {
      title: '1. Introduction and scope',
      body: `1.1 Velox Sàrl ("Velox", "we", "us" or "our") operates a digital freight orchestration platform connecting Shippers with independent road transport Carriers, accessible via our website, our Carrier Portal, and our mobile application (together, the "Services"). This Privacy Notice is issued in accordance with Regulation (EU) 2016/679 (GDPR) and the Luxembourg Act of 1 August 2018.

1.2 This Privacy Notice explains what personal data we collect, why we collect it, how we use it, who we share it with, and what rights you have, when you:
(a) visit our website;
(b) register for, or use, the Carrier Portal or the App as a Carrier or a representative, driver, or other individual acting on behalf of a Carrier;
(c) act as a representative, employee, or contact person of a Shipper using Velox's services under a Master Services Agreement;
(d) are a member of Velox's staff, or a contractor engaged by Velox; or
(e) otherwise correspond with us.

1.3 This Privacy Notice does not apply to information Velox processes purely on behalf of, and under the instructions of, a Shipper as a data processor. That processing is governed by the data processing agreement in place with the relevant Shipper.

1.4 This Privacy Notice does not govern the processing of personal data by Carriers, Shippers, or other third parties on their own systems, for which they are separately responsible as independent data controllers.`,
    },
    {
      title: '2. Who we are',
      body: `Velox Sàrl is a private limited liability company (société à responsabilité limitée) incorporated under the laws of the Grand Duchy of Luxembourg, with its registered office at 38, rue Wilson, L-2732 Luxembourg, registered with the Luxembourg Trade and Companies Register under number B306257. For the personal data described in this Privacy Notice, Velox acts as the data controller, except where expressly stated otherwise (see Section 1.3).`,
    },
    {
      title: '3. Data protection contact',
      body: `Velox Sàrl — Data Protection Contact
38, rue Wilson, L-2732 Luxembourg, Grand Duchy of Luxembourg
Email: info@myvelox.eu`,
    },
    {
      title: '4. Who this notice applies to',
      body: `We collect and use personal data about website visitors, Carriers and their representatives and drivers, Shipper representatives, and Velox staff and contractors.`,
    },
    {
      title: '5. Personal data we collect',
      body: `5.1 Website visitors. IP address, browser and device information, pages visited, and similar usage data, including through cookies and similar technologies (see Section 8).

5.2 Carrier registration and vetting:
• Identification and contact data — company name, registration number, VAT number, registered address, contact person name, email, and telephone number.
• Identity documents — passport or national identity card, and evidence of legal capacity and power of representation.
• Regulatory and compliance data — transport licences, permits, insurance certificates, sanctions-screening results, and other vetting documentation.
• Financial data — bank account details, used for payment of Transport Agreements.
• Fleet data — vehicle registration, type, capacity, and equipment information.

5.3 Carrier Portal usage:
• Carrier Content — proof of delivery, insurance certificates, and other documents uploaded to the Portal.
• GPS Data — location data from vehicles used to perform Transport Agreements.
• Performance data — the Carrier's Score and the underlying metrics (e.g., on-time performance, acceptance rate).
• Account and log data — login credentials (encrypted), access logs, and device/browser information.

5.4 Driver data. Where drivers are identified in Carrier Content or GPS Data, we process this data as described in this Notice. Carriers are responsible for informing their drivers of this processing and, where required, obtaining any necessary consents.

5.5 Shipper representative data. Name, business contact details, and role of the Shipper's authorised users and operational contacts.

5.6 Correspondence. If you contact us directly, we collect the information you provide.

5.7 Staff and contractor data. Identification and contact data, and employment-related information (payroll, HR, social security and tax records).`,
    },
    {
      title: '6. How and why we use personal data',
      body: `• Registering and vetting Carriers, including sanctions and compliance screening — performance of a contract (art. 6(1)(b)) and legal obligation (art. 6(1)(c)).
• Operating the Portal and the App, and enabling Carriers to book, accept, and manage Transport Agreements — performance of a contract (art. 6(1)(b)).
• Providing shipment visibility to Shippers, managing transports, and optimising route planning — performance of a contract (art. 6(1)(b)) and our legitimate interest (art. 6(1)(f)).
• Calculating and using the Carrier Score — our legitimate interest in service quality and reliability (art. 6(1)(f)).
• Invoicing, payment administration, and accounting — performance of a contract (art. 6(1)(b)) and legal obligation (art. 6(1)(c)).
• Detecting and preventing fraud and monitoring breaches — our legitimate interest (art. 6(1)(f)).
• Complying with sanctions, anti-money-laundering, and other regulatory obligations — legal obligation (art. 6(1)(c)).
• Responding to correspondence and support requests — art. 6(1)(b) or 6(1)(f).
• Direct marketing communications — your consent (art. 6(1)(a)), where required.
• Cookies and website analytics — your consent (art. 6(1)(a)), except strictly necessary cookies (art. 6(1)(f)).
• Managing employment and contractor relationships — art. 6(1)(b) and 6(1)(c).

Where we exceptionally process special categories of personal data within the meaning of article 9 GDPR, we rely on the applicable derogations, such as the establishment, exercise, or defence of legal claims, or a substantial public interest.`,
    },
    {
      title: '7. Automated decision-making and profiling',
      body: `7.1 We calculate a Score for each Carrier based on service quality and reliability metrics (e.g., GPS coverage, on-time performance, acceptance rate, disruptions). This constitutes a form of profiling.

7.2 The Score may affect which booking options (e.g., "Book Now") are available to a Carrier. We do not use the Score to make decisions producing legal effects, or similarly significant effects, based solely on automated processing without the possibility of human review. If you believe a decision has been made in error, you may contact your carrier contact manager or our Data Protection Contact to request a review.`,
    },
    {
      title: '8. Cookies and similar technologies',
      body: `8.1 Our website and Portal use cookies and similar technologies, including strictly necessary cookies (e.g., for security and session management, provided with the help of Cloudflare and our hosting platform) and, where you consent, analytics cookies (currently provided by PostHog).

8.2 Where required by applicable law, we ask for your consent before placing non-essential cookies and provide a cookie management tool to accept, decline, or change your choices at any time. Strictly necessary cookies do not require consent.

8.3 Further details are set out in our Cookie Policy, available via a dedicated link on the Website and Portal.`,
    },
    {
      title: '9. Who we share personal data with',
      body: `We may share personal data with the following categories of recipients, always on a need-to-know basis:

(a) Carriers and Shippers — to the extent necessary to perform a Transport Agreement or Freight Order;
(b) Service providers acting on our behalf — cloud infrastructure and hosting, payment and invoicing, communication and notification tools, and security and analytics services, currently including Cloudflare (security/bot-management) and PostHog (analytics);
(c) Financial and insurance partners, including factoring partners and insurance providers;
(d) Professional advisors, including auditors, lawyers, and accountants;
(e) Public authorities, including tax, customs, and law enforcement authorities, where required by law; and
(f) A successor entity, in the context of a merger, acquisition, or sale of all or part of Velox's business.

We do not sell personal data to third parties.`,
    },
    {
      title: '10. International data transfers',
      body: `10.1 We primarily process personal data within the European Union and the European Economic Area ("EEA"). Based on our current service providers and infrastructure (including our hosting platform, Cloudflare, and PostHog's EU region), personal data collected through the Website and Portal is currently processed within the EEA (specifically, Germany).

10.2 Where personal data is or becomes transferred outside the EEA, we ensure an appropriate legal mechanism is in place, such as an adequacy decision of the European Commission, the European Commission's Standard Contractual Clauses, or another safeguard recognised under the GDPR.`,
    },
    {
      title: '11. Data retention',
      body: `11.1 We retain personal data only for as long as necessary for the purposes described in this Notice, taking into account the duration of the contractual relationship, applicable statutory retention periods (currently ten (10) years for accounting and invoicing records under Luxembourg law), and our legitimate interest in legal claims.

11.2 As a general approach:
• Registration and vetting documentation, and records relating to Transport Agreements and Orders — duration of the relationship plus five (5) years.
• GPS Data and Carrier Content relating to a specific Transport Agreement — as long as necessary for performance and verification, and in any event no longer than five (5) years.
• Score and performance metrics — rolling calculation period, plus a limited historical record capped at five (5) years.
• Invoicing and financial records — ten (10) years.
• Staff and contractor records — duration of the engagement plus at least five (5) years.
• Website analytics/cookie data — as set out in the Cookie Policy.`,
    },
    {
      title: '12. Data security',
      body: `12.1 We implement appropriate technical and organisational measures designed to protect personal data against unauthorised access, accidental loss, unlawful disclosure, and alteration or destruction, including:
(a) password-protected and, where appropriate, encrypted systems;
(b) secure document storage and management;
(c) access controls restricting access on a need-to-know basis; and
(d) staff awareness of data protection obligations.

12.2 In the event of a personal data breach likely to result in a risk to the rights and freedoms of individuals, we will notify the CNPD within seventy-two (72) hours of becoming aware of the breach and, where required, inform the affected individuals without undue delay.`,
    },
    {
      title: '13. Your rights',
      body: `13.1 Subject to the conditions set out in the GDPR, you have the right to:
(a) request access to the personal data we hold about you;
(b) request rectification of inaccurate or incomplete personal data;
(c) request erasure of your personal data;
(d) request restriction of processing;
(e) object to processing based on our legitimate interest, including profiling, and to direct marketing;
(f) request portability of personal data you have provided to us, where technically feasible; and
(g) withdraw your consent at any time, where processing is based on consent.

13.2 To exercise these rights, contact us using the details in Section 3. We may need to verify your identity. We will respond within one (1) month of receipt, extendable by two (2) further months where necessary.

13.3 Some of these rights may be limited where we are required to retain or process data by law, or to establish, exercise, or defend legal claims.

13.4 You also have the right to lodge a complaint with a supervisory authority. In Luxembourg:
Commission nationale pour la protection des données (CNPD)
15, Boulevard du Jazz, L-4370 Belvaux, Grand Duchy of Luxembourg
Telephone: +352 26 10 60 1 — Email: info@cnpd.lu — www.cnpd.public.lu`,
    },
    {
      title: '14. Children',
      body: `Our Services are intended for use by businesses and professionals only. Minors and consumers are not permitted to register on the Portal, and we do not knowingly collect personal data from children.`,
    },
    {
      title: '15. Changes to this Privacy Notice',
      body: `We will review this Privacy Notice at least annually, and will update it where necessary to reflect changes to our Services, our activities or systems, or applicable law. We will publish the updated version on our website and/or the Portal, together with its effective date.`,
    },
    {
      title: '16. Contact us',
      body: `Velox Sàrl
38, rue Wilson, L-2732 Luxembourg, Grand Duchy of Luxembourg
Email: info@myvelox.eu`,
    },
  ],
};

const fr: PrivacyNoticeContent = {
  title: 'Politique de confidentialité',
  subtitle: 'Applicable au site internet, au portail transporteur, à l’application et aux services associés',
  lastUpdated: 'Dernière mise à jour : 31.08.2026',
  back: "Retour à l'accueil",
  sections: [
    {
      title: '1. Introduction et champ d’application',
      body: `1.1 Velox Sàrl (« Velox », « nous ») exploite une plateforme numérique d'orchestration du fret mettant en relation des expéditeurs et des transporteurs routiers indépendants, accessible via notre site internet, notre portail transporteur et notre application mobile (ensemble, les « services »). La présente politique est établie conformément au règlement (UE) 2016/679 (RGPD) et à la loi luxembourgeoise du 1er août 2018.

1.2 Elle explique quelles données à caractère personnel nous collectons, pourquoi, comment nous les utilisons, avec qui nous les partageons et quels sont vos droits, lorsque vous :
(a) visitez notre site internet ;
(b) vous inscrivez ou utilisez le portail transporteur ou l'application en tant que transporteur, représentant, chauffeur ou autre personne agissant pour un transporteur ;
(c) agissez en tant que représentant, employé ou contact d'un expéditeur utilisant les services de Velox ;
(d) êtes membre du personnel de Velox ou prestataire engagé par Velox ; ou
(e) correspondez avec nous.

1.3 La présente politique ne s'applique pas aux informations que Velox traite exclusivement pour le compte et sur instruction d'un expéditeur en qualité de sous-traitant. Ce traitement est régi par l'accord de traitement des données conclu avec l'expéditeur concerné.

1.4 Elle ne régit pas le traitement des données par les transporteurs, expéditeurs ou autres tiers sur leurs propres systèmes, dont ils sont responsables en tant que responsables de traitement indépendants.`,
    },
    {
      title: '2. Qui nous sommes',
      body: `Velox Sàrl est une société à responsabilité limitée de droit luxembourgeois, dont le siège social est situé 38, rue Wilson, L-2732 Luxembourg, immatriculée au Registre de Commerce et des Sociétés de Luxembourg sous le numéro B306257. Pour les données décrites dans la présente politique, Velox agit en qualité de responsable du traitement, sauf indication contraire (voir section 1.3).`,
    },
    {
      title: '3. Contact protection des données',
      body: `Velox Sàrl — Contact protection des données
38, rue Wilson, L-2732 Luxembourg, Grand-Duché de Luxembourg
Courriel : info@myvelox.eu`,
    },
    {
      title: '4. Personnes concernées',
      body: `Nous collectons et utilisons des données concernant les visiteurs du site, les transporteurs, leurs représentants et chauffeurs, les représentants des expéditeurs, ainsi que le personnel et les prestataires de Velox.`,
    },
    {
      title: '5. Données que nous collectons',
      body: `5.1 Visiteurs du site. Adresse IP, informations sur le navigateur et l'appareil, pages consultées et données d'utilisation similaires, notamment via les cookies (voir section 8).

5.2 Inscription et vérification des transporteurs :
• Données d'identification et de contact — dénomination sociale, numéro d'immatriculation, numéro de TVA, adresse du siège, nom du contact, courriel et téléphone.
• Documents d'identité — passeport ou carte d'identité, et preuve de la capacité juridique et du pouvoir de représentation.
• Données réglementaires et de conformité — licences de transport, autorisations, attestations d'assurance, résultats des contrôles de sanctions et autre documentation de vérification.
• Données financières — coordonnées bancaires, utilisées pour le paiement des contrats de transport.
• Données de flotte — immatriculation, type, capacité et équipements des véhicules.

5.3 Utilisation du portail transporteur :
• Contenus transporteur — preuves de livraison, attestations d'assurance et autres documents téléversés.
• Données GPS — données de localisation des véhicules utilisés pour exécuter les contrats de transport.
• Données de performance — le score du transporteur et les indicateurs sous-jacents (ponctualité, taux d'acceptation).
• Données de compte et de journalisation — identifiants (chiffrés), journaux d'accès, informations appareil/navigateur.

5.4 Données des chauffeurs. Lorsque des chauffeurs sont identifiés dans les contenus transporteur ou les données GPS, nous traitons ces données comme décrit ici. Les transporteurs sont responsables d'informer leurs chauffeurs et, le cas échéant, de recueillir les consentements nécessaires.

5.5 Données des représentants d'expéditeurs. Nom, coordonnées professionnelles et fonction des utilisateurs autorisés et contacts opérationnels.

5.6 Correspondance. Si vous nous contactez, nous collectons les informations que vous fournissez.

5.7 Personnel et prestataires. Données d'identification et de contact, et informations liées à l'emploi (paie, RH, sécurité sociale et fiscalité).`,
    },
    {
      title: '6. Finalités et bases légales',
      body: `• Inscription et vérification des transporteurs, y compris les contrôles de sanctions et de conformité — exécution d'un contrat (art. 6(1)(b)) et obligation légale (art. 6(1)(c)).
• Exploitation du portail et de l'application, réservation et gestion des contrats de transport — exécution d'un contrat (art. 6(1)(b)).
• Visibilité des expéditions pour les expéditeurs, gestion des transports et optimisation des itinéraires — art. 6(1)(b) et intérêt légitime (art. 6(1)(f)).
• Calcul et utilisation du score transporteur — intérêt légitime lié à la qualité et à la fiabilité du service (art. 6(1)(f)).
• Facturation, gestion des paiements et comptabilité — art. 6(1)(b) et 6(1)(c).
• Détection et prévention de la fraude, surveillance des manquements — intérêt légitime (art. 6(1)(f)).
• Respect des sanctions, de la lutte anti-blanchiment et d'autres obligations réglementaires — obligation légale (art. 6(1)(c)).
• Réponse aux correspondances et demandes d'assistance — art. 6(1)(b) ou 6(1)(f).
• Communications de marketing direct — votre consentement (art. 6(1)(a)), lorsque requis.
• Cookies et analyse d'audience — votre consentement (art. 6(1)(a)), sauf cookies strictement nécessaires (art. 6(1)(f)).
• Gestion des relations d'emploi et de prestation — art. 6(1)(b) et 6(1)(c).

Lorsque nous traitons exceptionnellement des catégories particulières de données au sens de l'article 9 du RGPD, nous nous fondons sur les dérogations applicables, telles que la constatation, l'exercice ou la défense de droits en justice, ou un motif d'intérêt public important.`,
    },
    {
      title: '7. Décision automatisée et profilage',
      body: `7.1 Nous calculons un score pour chaque transporteur à partir d'indicateurs de qualité et de fiabilité (couverture GPS, ponctualité, taux d'acceptation, incidents). Cela constitue une forme de profilage.

7.2 Le score peut influencer les options de réservation disponibles (par exemple « Réserver maintenant »). Nous n'utilisons pas le score pour prendre des décisions produisant des effets juridiques ou vous affectant de manière significative, fondées exclusivement sur un traitement automatisé sans possibilité d'intervention humaine. Si vous estimez qu'une décision est erronée, vous pouvez demander un réexamen auprès de votre gestionnaire de contact ou de notre contact protection des données.`,
    },
    {
      title: '8. Cookies et technologies similaires',
      body: `8.1 Notre site et notre portail utilisent des cookies et technologies similaires, notamment des cookies strictement nécessaires (sécurité et gestion de session, avec l'aide de Cloudflare et de notre hébergeur) et, avec votre consentement, des cookies de mesure d'audience (actuellement PostHog).

8.2 Lorsque la loi l'exige, nous recueillons votre consentement avant le dépôt de cookies non essentiels et mettons à disposition un outil de gestion permettant d'accepter, de refuser ou de modifier vos choix à tout moment. Les cookies strictement nécessaires ne requièrent pas de consentement.

8.3 Le détail figure dans notre politique en matière de cookies, accessible via un lien dédié sur le site et le portail.`,
    },
    {
      title: '9. Destinataires des données',
      body: `Nous pouvons partager des données avec les catégories de destinataires suivantes, toujours selon le principe du besoin d'en connaître :

(a) transporteurs et expéditeurs — dans la mesure nécessaire à l'exécution d'un contrat de transport ou d'une commande ;
(b) prestataires agissant pour notre compte — infrastructure cloud et hébergement, paiement et facturation, outils de communication et de notification, sécurité et analyse, actuellement Cloudflare (sécurité/gestion des bots) et PostHog (analyse) ;
(c) partenaires financiers et d'assurance, y compris les partenaires d'affacturage et les assureurs ;
(d) conseils professionnels, dont auditeurs, avocats et comptables ;
(e) autorités publiques, notamment fiscales, douanières et judiciaires, lorsque la loi l'exige ; et
(f) une entité repreneuse, dans le cadre d'une fusion, acquisition ou cession de tout ou partie de l'activité de Velox.

Nous ne vendons pas de données à caractère personnel à des tiers.`,
    },
    {
      title: '10. Transferts internationaux',
      body: `10.1 Nous traitons principalement les données au sein de l'Union européenne et de l'Espace économique européen (« EEE »). Compte tenu de nos prestataires et infrastructures actuels (hébergeur, Cloudflare, région UE de PostHog), les données collectées via le site et le portail sont actuellement traitées dans l'EEE (en Allemagne).

10.2 Lorsque des données sont transférées hors de l'EEE, nous veillons à ce qu'un mécanisme juridique approprié soit en place : décision d'adéquation de la Commission européenne, clauses contractuelles types ou autre garantie reconnue par le RGPD.`,
    },
    {
      title: '11. Durées de conservation',
      body: `11.1 Nous conservons les données uniquement le temps nécessaire aux finalités décrites, en tenant compte de la durée de la relation contractuelle, des durées légales de conservation (actuellement dix (10) ans pour les documents comptables et de facturation au Luxembourg) et de notre intérêt légitime en matière de contentieux.

11.2 En règle générale :
• Documentation d'inscription et de vérification, contrats de transport et commandes — durée de la relation plus cinq (5) ans.
• Données GPS et contenus transporteur liés à un transport — le temps nécessaire à l'exécution et à la vérification, et en tout état de cause cinq (5) ans maximum.
• Score et indicateurs de performance — période glissante de calcul, plus un historique limité plafonné à cinq (5) ans.
• Documents de facturation et financiers — dix (10) ans.
• Dossiers du personnel et des prestataires — durée de la relation plus au moins cinq (5) ans.
• Données d'analyse d'audience et cookies — comme indiqué dans la politique cookies.`,
    },
    {
      title: '12. Sécurité des données',
      body: `12.1 Nous mettons en œuvre des mesures techniques et organisationnelles appropriées afin de protéger les données contre tout accès non autorisé, perte accidentelle, divulgation illicite, altération ou destruction, notamment :
(a) des systèmes protégés par mot de passe et, le cas échéant, chiffrés ;
(b) un stockage et une gestion documentaire sécurisés ;
(c) des contrôles d'accès limités au besoin d'en connaître ; et
(d) la sensibilisation du personnel aux obligations de protection des données.

12.2 En cas de violation de données susceptible d'engendrer un risque pour les droits et libertés des personnes, nous notifierons la CNPD dans les soixante-douze (72) heures suivant la prise de connaissance et informerons, si nécessaire, les personnes concernées dans les meilleurs délais.`,
    },
    {
      title: '13. Vos droits',
      body: `13.1 Sous réserve des conditions du RGPD, vous avez le droit :
(a) d'accéder aux données vous concernant ;
(b) de demander la rectification de données inexactes ou incomplètes ;
(c) de demander l'effacement de vos données ;
(d) de demander la limitation du traitement ;
(e) de vous opposer au traitement fondé sur notre intérêt légitime, y compris au profilage, et au marketing direct ;
(f) de demander la portabilité des données que vous nous avez fournies, lorsque cela est techniquement possible ; et
(g) de retirer votre consentement à tout moment, lorsque le traitement repose sur celui-ci.

13.2 Pour exercer ces droits, contactez-nous aux coordonnées de la section 3. Nous pouvons devoir vérifier votre identité. Nous répondons dans un délai d'un (1) mois, prolongeable de deux (2) mois si nécessaire.

13.3 Certains droits peuvent être limités lorsque la loi nous impose de conserver ou de traiter des données, ou pour la constatation, l'exercice ou la défense de droits en justice.

13.4 Vous avez également le droit d'introduire une réclamation auprès d'une autorité de contrôle. Au Luxembourg :
Commission nationale pour la protection des données (CNPD)
15, Boulevard du Jazz, L-4370 Belvaux, Grand-Duché de Luxembourg
Téléphone : +352 26 10 60 1 — Courriel : info@cnpd.lu — www.cnpd.public.lu`,
    },
    {
      title: '14. Mineurs',
      body: `Nos services sont destinés exclusivement aux entreprises et professionnels. Les mineurs et les consommateurs ne sont pas autorisés à s'inscrire sur le portail et nous ne collectons pas sciemment de données concernant des enfants.`,
    },
    {
      title: '15. Modifications de la présente politique',
      body: `Nous réexaminons cette politique au moins une fois par an et la mettons à jour lorsque cela est nécessaire pour refléter l'évolution de nos services, de nos activités ou systèmes, ou de la législation applicable. La version mise à jour est publiée sur notre site et/ou le portail, avec sa date d'entrée en vigueur.`,
    },
    {
      title: '16. Nous contacter',
      body: `Velox Sàrl
38, rue Wilson, L-2732 Luxembourg, Grand-Duché de Luxembourg
Courriel : info@myvelox.eu`,
    },
  ],
};

const es: PrivacyNoticeContent = {
  title: 'Política de privacidad',
  subtitle: 'Aplicable al sitio web, al portal de transportistas, a la app y a los servicios relacionados',
  lastUpdated: 'Última actualización: 31.08.2026',
  back: 'Volver al inicio',
  sections: [
    {
      title: '1. Introducción y ámbito',
      body: `1.1 Velox Sàrl («Velox», «nosotros») opera una plataforma digital de orquestación de transporte de mercancías que conecta a cargadores con transportistas de carretera independientes, accesible a través de nuestro sitio web, nuestro portal de transportistas y nuestra aplicación móvil (conjuntamente, los «servicios»). Esta política se emite conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley luxemburguesa de 1 de agosto de 2018.

1.2 Explica qué datos personales recopilamos, por qué, cómo los usamos, con quién los compartimos y qué derechos le asisten cuando: (a) visita nuestro sitio web; (b) se registra o utiliza el portal o la app como transportista, representante, conductor u otra persona que actúa por cuenta de un transportista; (c) actúa como representante, empleado o contacto de un cargador; (d) es personal o contratista de Velox; o (e) se comunica con nosotros.

1.3 No se aplica a la información que Velox trata exclusivamente por cuenta y siguiendo instrucciones de un cargador como encargado del tratamiento.

1.4 No regula el tratamiento realizado por transportistas, cargadores u otros terceros en sus propios sistemas, de los que son responsables de forma independiente.`,
    },
    {
      title: '2. Quiénes somos',
      body: `Velox Sàrl es una sociedad de responsabilidad limitada de derecho luxemburgués, con domicilio social en 38, rue Wilson, L-2732 Luxemburgo, inscrita en el Registro Mercantil de Luxemburgo con el número B306257. Velox actúa como responsable del tratamiento, salvo indicación en contrario (véase la sección 1.3).`,
    },
    {
      title: '3. Contacto de protección de datos',
      body: `Velox Sàrl — Contacto de protección de datos
38, rue Wilson, L-2732 Luxemburgo, Gran Ducado de Luxemburgo
Correo electrónico: info@myvelox.eu`,
    },
    {
      title: '4. Personas a las que se aplica',
      body: `Tratamos datos de visitantes del sitio web, transportistas y sus representantes y conductores, representantes de cargadores, y personal y contratistas de Velox.`,
    },
    {
      title: '5. Datos personales que recopilamos',
      body: `5.1 Visitantes del sitio web. Dirección IP, información del navegador y del dispositivo, páginas visitadas y datos de uso similares, incluidas cookies (véase la sección 8).

5.2 Registro y verificación de transportistas: datos de identificación y contacto; documentos de identidad y prueba de capacidad y poder de representación; datos regulatorios y de cumplimiento (licencias, permisos, seguros, cribado de sanciones); datos financieros (cuenta bancaria); datos de flota (matrícula, tipo, capacidad, equipamiento).

5.3 Uso del portal: contenidos del transportista (pruebas de entrega, certificados de seguro y otros documentos); datos GPS de los vehículos; datos de rendimiento (puntuación y métricas); datos de cuenta y registros de acceso.

5.4 Datos de conductores. Cuando los conductores se identifican en los contenidos o en los datos GPS, tratamos estos datos según esta política. Los transportistas deben informar a sus conductores y obtener, en su caso, los consentimientos necesarios.

5.5 Datos de representantes de cargadores. Nombre, datos de contacto profesionales y función.

5.6 Correspondencia. Si nos contacta, recopilamos la información que nos facilita.

5.7 Personal y contratistas. Datos de identificación y contacto e información laboral (nóminas, RR. HH., seguridad social e impuestos).`,
    },
    {
      title: '6. Finalidades y bases jurídicas',
      body: `• Registro y verificación de transportistas, incluido el cribado de sanciones — ejecución de contrato (art. 6(1)(b)) y obligación legal (art. 6(1)(c)).
• Operación del portal y la app y gestión de los contratos de transporte — art. 6(1)(b).
• Visibilidad de los envíos, gestión del transporte y optimización de rutas — art. 6(1)(b) e interés legítimo (art. 6(1)(f)).
• Cálculo y uso de la puntuación del transportista — interés legítimo (art. 6(1)(f)).
• Facturación, pagos y contabilidad — art. 6(1)(b) y 6(1)(c).
• Prevención del fraude y control de incumplimientos — interés legítimo (art. 6(1)(f)).
• Cumplimiento de sanciones, prevención del blanqueo y otras obligaciones — art. 6(1)(c).
• Respuesta a consultas y soporte — art. 6(1)(b) o 6(1)(f).
• Marketing directo — su consentimiento (art. 6(1)(a)), cuando se requiera.
• Cookies y analítica web — su consentimiento (art. 6(1)(a)), salvo cookies estrictamente necesarias (art. 6(1)(f)).
• Gestión de relaciones laborales y de contratistas — art. 6(1)(b) y 6(1)(c).

Cuando excepcionalmente tratamos categorías especiales de datos (art. 9 RGPD), nos basamos en las excepciones aplicables, como la formulación, el ejercicio o la defensa de reclamaciones o un interés público esencial.`,
    },
    {
      title: '7. Decisiones automatizadas y elaboración de perfiles',
      body: `7.1 Calculamos una puntuación para cada transportista a partir de métricas de calidad y fiabilidad (cobertura GPS, puntualidad, tasa de aceptación, incidencias). Esto constituye una forma de elaboración de perfiles.

7.2 La puntuación puede afectar a las opciones de reserva disponibles. No la utilizamos para adoptar decisiones con efectos jurídicos o significativos basadas únicamente en el tratamiento automatizado sin posibilidad de revisión humana. Si considera que una decisión es errónea, puede solicitar su revisión.`,
    },
    {
      title: '8. Cookies y tecnologías similares',
      body: `8.1 Nuestro sitio y portal utilizan cookies estrictamente necesarias (seguridad y sesión, con Cloudflare y nuestra plataforma de alojamiento) y, con su consentimiento, cookies analíticas (actualmente PostHog).

8.2 Cuando la ley lo exige, solicitamos su consentimiento antes de instalar cookies no esenciales y ofrecemos una herramienta para aceptar, rechazar o modificar sus preferencias en cualquier momento.

8.3 El detalle figura en nuestra política de cookies, accesible mediante un enlace dedicado.`,
    },
    {
      title: '9. Con quién compartimos los datos',
      body: `(a) transportistas y cargadores, en la medida necesaria para ejecutar un contrato de transporte;
(b) proveedores de servicios que actúan por nuestra cuenta (alojamiento e infraestructura, pagos y facturación, comunicación, seguridad y analítica; actualmente Cloudflare y PostHog);
(c) socios financieros y aseguradores, incluidos socios de factoring;
(d) asesores profesionales (auditores, abogados, contables);
(e) autoridades públicas, cuando lo exija la ley; y
(f) una entidad sucesora, en el marco de una fusión, adquisición o venta.

No vendemos datos personales a terceros.`,
    },
    {
      title: '10. Transferencias internacionales',
      body: `10.1 Tratamos los datos principalmente en la Unión Europea y el Espacio Económico Europeo («EEE»). Con los proveedores e infraestructuras actuales, los datos recogidos a través del sitio y del portal se tratan actualmente en el EEE (concretamente en Alemania).

10.2 Cuando se transfieren datos fuera del EEE, garantizamos un mecanismo jurídico adecuado: decisión de adecuación, cláusulas contractuales tipo u otra garantía reconocida por el RGPD.`,
    },
    {
      title: '11. Conservación de datos',
      body: `11.1 Conservamos los datos solo el tiempo necesario, teniendo en cuenta la duración de la relación contractual, los plazos legales (actualmente diez (10) años para registros contables y de facturación) y nuestro interés legítimo en reclamaciones.

11.2 Como criterio general:
• Documentación de registro y verificación y contratos de transporte — duración de la relación más cinco (5) años.
• Datos GPS y contenidos del transportista — máximo cinco (5) años.
• Puntuación y métricas — periodo de cálculo más un histórico limitado, máximo cinco (5) años.
• Registros financieros y de facturación — diez (10) años.
• Registros de personal y contratistas — duración de la relación más al menos cinco (5) años.
• Datos de analítica y cookies — según la política de cookies.`,
    },
    {
      title: '12. Seguridad de los datos',
      body: `12.1 Aplicamos medidas técnicas y organizativas apropiadas: sistemas protegidos por contraseña y, cuando procede, cifrados; almacenamiento y gestión documental seguros; controles de acceso según el principio de necesidad de conocer; y concienciación del personal.

12.2 En caso de violación de datos que pueda suponer un riesgo para los derechos y libertades de las personas, notificaremos a la CNPD en un plazo de setenta y dos (72) horas y, si procede, informaremos a los afectados sin dilación indebida.`,
    },
    {
      title: '13. Sus derechos',
      body: `13.1 Conforme al RGPD, tiene derecho a: (a) acceder a sus datos; (b) solicitar su rectificación; (c) solicitar su supresión; (d) solicitar la limitación del tratamiento; (e) oponerse al tratamiento basado en interés legítimo, incluida la elaboración de perfiles, y al marketing directo; (f) solicitar la portabilidad; y (g) retirar su consentimiento en cualquier momento.

13.2 Para ejercerlos, contáctenos según la sección 3. Podemos necesitar verificar su identidad. Responderemos en el plazo de un (1) mes, prorrogable dos (2) meses adicionales.

13.3 Algunos derechos pueden estar limitados por obligaciones legales o por la defensa de reclamaciones.

13.4 También puede presentar una reclamación ante la autoridad de control. En Luxemburgo:
Commission nationale pour la protection des données (CNPD)
15, Boulevard du Jazz, L-4370 Belvaux — Tel.: +352 26 10 60 1 — info@cnpd.lu — www.cnpd.public.lu`,
    },
    {
      title: '14. Menores',
      body: `Nuestros servicios están destinados exclusivamente a empresas y profesionales. Los menores y consumidores no pueden registrarse en el portal y no recopilamos conscientemente datos de niños.`,
    },
    {
      title: '15. Cambios en esta política',
      body: `Revisamos esta política al menos anualmente y la actualizamos cuando sea necesario para reflejar cambios en nuestros servicios, actividades o sistemas, o en la legislación aplicable. Publicaremos la versión actualizada con su fecha de entrada en vigor.`,
    },
    {
      title: '16. Contacto',
      body: `Velox Sàrl
38, rue Wilson, L-2732 Luxemburgo, Gran Ducado de Luxemburgo
Correo electrónico: info@myvelox.eu`,
    },
  ],
};

const de: PrivacyNoticeContent = {
  title: 'Datenschutzerklärung',
  subtitle: 'Gültig für die Website, das Transporteur-Portal, die App und zugehörige Dienste',
  lastUpdated: 'Letzte Aktualisierung: 31.08.2026',
  back: 'Zurück zur Startseite',
  sections: [
    {
      title: '1. Einleitung und Anwendungsbereich',
      body: `1.1 Velox Sàrl („Velox", „wir") betreibt eine digitale Plattform zur Frachtorchestrierung, die Versender mit unabhängigen Straßentransportunternehmen verbindet, erreichbar über unsere Website, unser Transporteur-Portal und unsere mobile App (zusammen die „Dienste"). Diese Erklärung erfolgt nach der Verordnung (EU) 2016/679 (DSGVO) und dem luxemburgischen Gesetz vom 1. August 2018.

1.2 Sie erläutert, welche personenbezogenen Daten wir erheben, warum, wie wir sie verwenden, mit wem wir sie teilen und welche Rechte Sie haben, wenn Sie: (a) unsere Website besuchen; (b) sich als Transporteur, Vertreter, Fahrer oder sonstige für einen Transporteur handelnde Person registrieren oder das Portal bzw. die App nutzen; (c) als Vertreter, Mitarbeiter oder Ansprechpartner eines Versenders handeln; (d) Mitarbeiter oder Auftragnehmer von Velox sind; oder (e) mit uns korrespondieren.

1.3 Sie gilt nicht für Daten, die Velox ausschließlich im Auftrag und nach Weisung eines Versenders als Auftragsverarbeiter verarbeitet.

1.4 Sie regelt nicht die Verarbeitung durch Transporteure, Versender oder Dritte in deren eigenen Systemen, für die diese eigenständig verantwortlich sind.`,
    },
    {
      title: '2. Wer wir sind',
      body: `Velox Sàrl ist eine Gesellschaft mit beschränkter Haftung nach luxemburgischem Recht mit Sitz in 38, rue Wilson, L-2732 Luxemburg, eingetragen im Handels- und Gesellschaftsregister Luxemburg unter der Nummer B306257. Velox ist Verantwortlicher im Sinne der DSGVO, sofern nicht ausdrücklich anders angegeben (siehe Abschnitt 1.3).`,
    },
    {
      title: '3. Datenschutzkontakt',
      body: `Velox Sàrl — Datenschutzkontakt
38, rue Wilson, L-2732 Luxemburg, Großherzogtum Luxemburg
E-Mail: info@myvelox.eu`,
    },
    {
      title: '4. Für wen diese Erklärung gilt',
      body: `Wir verarbeiten Daten von Website-Besuchern, Transporteuren sowie deren Vertretern und Fahrern, Vertretern von Versendern sowie Mitarbeitern und Auftragnehmern von Velox.`,
    },
    {
      title: '5. Welche Daten wir erheben',
      body: `5.1 Website-Besucher. IP-Adresse, Browser- und Geräteinformationen, besuchte Seiten und ähnliche Nutzungsdaten, auch über Cookies (siehe Abschnitt 8).

5.2 Registrierung und Prüfung von Transporteuren: Identifikations- und Kontaktdaten; Ausweisdokumente sowie Nachweise der Rechts- und Vertretungsbefugnis; Regulierungs- und Compliance-Daten (Lizenzen, Genehmigungen, Versicherungsnachweise, Sanktionsprüfungen); Finanzdaten (Bankverbindung); Flottendaten (Kennzeichen, Typ, Kapazität, Ausstattung).

5.3 Nutzung des Portals: Transporteur-Inhalte (Liefernachweise, Versicherungsnachweise, weitere Dokumente); GPS-Daten der eingesetzten Fahrzeuge; Leistungsdaten (Score und zugrunde liegende Kennzahlen); Konto- und Protokolldaten.

5.4 Fahrerdaten. Soweit Fahrer in Transporteur-Inhalten oder GPS-Daten identifiziert werden, verarbeiten wir diese Daten wie beschrieben. Transporteure müssen ihre Fahrer informieren und erforderliche Einwilligungen einholen.

5.5 Daten von Versender-Vertretern. Name, geschäftliche Kontaktdaten und Funktion.

5.6 Korrespondenz. Wenn Sie uns kontaktieren, erheben wir die von Ihnen angegebenen Informationen.

5.7 Mitarbeiter- und Auftragnehmerdaten. Identifikations- und Kontaktdaten sowie beschäftigungsbezogene Informationen (Lohn, HR, Sozialversicherung, Steuern).`,
    },
    {
      title: '6. Zwecke und Rechtsgrundlagen',
      body: `• Registrierung und Prüfung von Transporteuren, einschließlich Sanktions- und Compliance-Screening — Vertragserfüllung (Art. 6(1)(b)) und rechtliche Verpflichtung (Art. 6(1)(c)).
• Betrieb des Portals und der App sowie Buchung und Verwaltung von Transportverträgen — Art. 6(1)(b).
• Sendungstransparenz für Versender, Transportsteuerung und Routenoptimierung — Art. 6(1)(b) und berechtigtes Interesse (Art. 6(1)(f)).
• Berechnung und Nutzung des Transporteur-Scores — berechtigtes Interesse (Art. 6(1)(f)).
• Rechnungsstellung, Zahlungsabwicklung und Buchhaltung — Art. 6(1)(b) und 6(1)(c).
• Betrugserkennung und -prävention — berechtigtes Interesse (Art. 6(1)(f)).
• Einhaltung von Sanktions-, Geldwäsche- und sonstigen regulatorischen Pflichten — Art. 6(1)(c).
• Beantwortung von Anfragen und Support — Art. 6(1)(b) oder 6(1)(f).
• Direktmarketing — Ihre Einwilligung (Art. 6(1)(a)), soweit erforderlich.
• Cookies und Webanalyse — Ihre Einwilligung (Art. 6(1)(a)), außer bei unbedingt erforderlichen Cookies (Art. 6(1)(f)).
• Verwaltung von Beschäftigungs- und Auftragsverhältnissen — Art. 6(1)(b) und 6(1)(c).

Verarbeiten wir ausnahmsweise besondere Kategorien personenbezogener Daten (Art. 9 DSGVO), stützen wir uns auf die einschlägigen Ausnahmen, etwa die Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen oder ein erhebliches öffentliches Interesse.`,
    },
    {
      title: '7. Automatisierte Entscheidungsfindung und Profiling',
      body: `7.1 Wir berechnen für jeden Transporteur einen Score auf Basis von Qualitäts- und Zuverlässigkeitskennzahlen (GPS-Abdeckung, Pünktlichkeit, Annahmequote, Störungen). Dies stellt eine Form des Profilings dar.

7.2 Der Score kann beeinflussen, welche Buchungsoptionen verfügbar sind. Wir treffen keine ausschließlich automatisierten Entscheidungen mit rechtlicher oder ähnlich erheblicher Wirkung ohne Möglichkeit menschlicher Überprüfung. Bei vermuteten Fehlern können Sie eine Überprüfung verlangen.`,
    },
    {
      title: '8. Cookies und ähnliche Technologien',
      body: `8.1 Unsere Website und unser Portal verwenden unbedingt erforderliche Cookies (Sicherheit und Sitzungsverwaltung, unterstützt durch Cloudflare und unsere Hosting-Plattform) sowie – mit Ihrer Einwilligung – Analyse-Cookies (derzeit PostHog).

8.2 Soweit gesetzlich erforderlich, holen wir vor dem Setzen nicht erforderlicher Cookies Ihre Einwilligung ein und stellen ein Verwaltungstool bereit, mit dem Sie Ihre Auswahl jederzeit ändern können.

8.3 Einzelheiten finden Sie in unserer Cookie-Richtlinie.`,
    },
    {
      title: '9. Empfänger der Daten',
      body: `(a) Transporteure und Versender, soweit zur Durchführung eines Transportvertrags erforderlich;
(b) Dienstleister in unserem Auftrag (Cloud-Infrastruktur und Hosting, Zahlung und Rechnungsstellung, Kommunikation, Sicherheit und Analyse; derzeit Cloudflare und PostHog);
(c) Finanz- und Versicherungspartner, einschließlich Factoring-Partner;
(d) Berufsgeheimnisträger wie Wirtschaftsprüfer, Rechtsanwälte und Steuerberater;
(e) Behörden, soweit gesetzlich vorgeschrieben; und
(f) ein Rechtsnachfolger im Rahmen einer Fusion, Übernahme oder Veräußerung.

Wir verkaufen keine personenbezogenen Daten an Dritte.`,
    },
    {
      title: '10. Internationale Datenübermittlungen',
      body: `10.1 Wir verarbeiten personenbezogene Daten überwiegend in der Europäischen Union und im Europäischen Wirtschaftsraum („EWR"). Auf Basis unserer derzeitigen Dienstleister und Infrastruktur werden über Website und Portal erhobene Daten derzeit im EWR (konkret in Deutschland) verarbeitet.

10.2 Bei Übermittlungen außerhalb des EWR stellen wir einen geeigneten Rechtsmechanismus sicher, etwa einen Angemessenheitsbeschluss, Standardvertragsklauseln oder eine andere nach der DSGVO anerkannte Garantie.`,
    },
    {
      title: '11. Speicherdauer',
      body: `11.1 Wir speichern Daten nur so lange wie erforderlich, unter Berücksichtigung der Vertragsdauer, gesetzlicher Aufbewahrungsfristen (derzeit zehn (10) Jahre für Buchhaltungs- und Rechnungsunterlagen) und unseres berechtigten Interesses an der Rechtsverteidigung.

11.2 Grundsätzlich gilt:
• Registrierungs- und Prüfunterlagen sowie Transportverträge — Dauer der Beziehung plus fünf (5) Jahre.
• GPS-Daten und Transporteur-Inhalte — höchstens fünf (5) Jahre.
• Score und Leistungskennzahlen — Berechnungszeitraum plus begrenzte Historie, maximal fünf (5) Jahre.
• Rechnungs- und Finanzunterlagen — zehn (10) Jahre.
• Personal- und Auftragnehmerunterlagen — Dauer des Verhältnisses plus mindestens fünf (5) Jahre.
• Analyse-/Cookie-Daten — gemäß Cookie-Richtlinie.`,
    },
    {
      title: '12. Datensicherheit',
      body: `12.1 Wir setzen geeignete technische und organisatorische Maßnahmen um: passwortgeschützte und, wo angemessen, verschlüsselte Systeme; sichere Dokumentenspeicherung; Zugriffskontrollen nach dem Need-to-know-Prinzip; und Sensibilisierung der Mitarbeitenden.

12.2 Bei einer Verletzung des Schutzes personenbezogener Daten mit voraussichtlichem Risiko für Rechte und Freiheiten melden wir dies der CNPD innerhalb von zweiundsiebzig (72) Stunden und informieren erforderlichenfalls die Betroffenen unverzüglich.`,
    },
    {
      title: '13. Ihre Rechte',
      body: `13.1 Nach Maßgabe der DSGVO haben Sie das Recht auf: (a) Auskunft; (b) Berichtigung; (c) Löschung; (d) Einschränkung der Verarbeitung; (e) Widerspruch gegen Verarbeitungen auf Grundlage berechtigter Interessen, einschließlich Profiling, und gegen Direktwerbung; (f) Datenübertragbarkeit; und (g) Widerruf Ihrer Einwilligung.

13.2 Zur Ausübung kontaktieren Sie uns über die Angaben in Abschnitt 3. Wir müssen ggf. Ihre Identität prüfen und antworten innerhalb eines (1) Monats, verlängerbar um zwei (2) Monate.

13.3 Einzelne Rechte können eingeschränkt sein, wenn gesetzliche Aufbewahrungspflichten bestehen oder Rechtsansprüche geltend gemacht oder verteidigt werden.

13.4 Sie haben zudem das Recht auf Beschwerde bei einer Aufsichtsbehörde. In Luxemburg:
Commission nationale pour la protection des données (CNPD)
15, Boulevard du Jazz, L-4370 Belvaux — Tel.: +352 26 10 60 1 — info@cnpd.lu — www.cnpd.public.lu`,
    },
    {
      title: '14. Kinder',
      body: `Unsere Dienste richten sich ausschließlich an Unternehmen und Gewerbetreibende. Minderjährige und Verbraucher dürfen sich nicht im Portal registrieren; wir erheben wissentlich keine Daten von Kindern.`,
    },
    {
      title: '15. Änderungen dieser Erklärung',
      body: `Wir überprüfen diese Datenschutzerklärung mindestens jährlich und aktualisieren sie bei Bedarf, um Änderungen unserer Dienste, Tätigkeiten, Systeme oder der Rechtslage abzubilden. Die aktualisierte Fassung wird mit Datum des Inkrafttretens veröffentlicht.`,
    },
    {
      title: '16. Kontakt',
      body: `Velox Sàrl
38, rue Wilson, L-2732 Luxemburg, Großherzogtum Luxemburg
E-Mail: info@myvelox.eu`,
    },
  ],
};

const it: PrivacyNoticeContent = {
  title: 'Informativa sulla privacy',
  subtitle: 'Applicabile al sito web, al portale trasportatori, all’app e ai servizi correlati',
  lastUpdated: 'Ultimo aggiornamento: 31.08.2026',
  back: 'Torna alla home',
  sections: [
    {
      title: '1. Introduzione e ambito',
      body: `1.1 Velox Sàrl («Velox», «noi») gestisce una piattaforma digitale di orchestrazione del trasporto merci che collega mittenti e trasportatori stradali indipendenti, accessibile tramite il nostro sito web, il portale trasportatori e l'app mobile (insieme, i «servizi»). La presente informativa è resa ai sensi del Regolamento (UE) 2016/679 (GDPR) e della legge lussemburghese del 1° agosto 2018.

1.2 Spiega quali dati personali raccogliamo, perché, come li utilizziamo, con chi li condividiamo e quali diritti avete quando: (a) visitate il nostro sito; (b) vi registrate o utilizzate il portale o l'app come trasportatore, rappresentante, autista o altro soggetto che agisce per conto di un trasportatore; (c) agite come rappresentante, dipendente o referente di un mittente; (d) siete personale o collaboratori di Velox; oppure (e) corrispondete con noi.

1.3 Non si applica alle informazioni che Velox tratta esclusivamente per conto e su istruzione di un mittente in qualità di responsabile del trattamento.

1.4 Non disciplina i trattamenti effettuati da trasportatori, mittenti o terzi sui propri sistemi, di cui essi sono autonomamente titolari.`,
    },
    {
      title: '2. Chi siamo',
      body: `Velox Sàrl è una società a responsabilità limitata di diritto lussemburghese, con sede legale in 38, rue Wilson, L-2732 Lussemburgo, iscritta al Registro del commercio e delle società del Lussemburgo con il numero B306257. Velox agisce come titolare del trattamento, salvo diversa indicazione (cfr. sezione 1.3).`,
    },
    {
      title: '3. Contatto per la protezione dei dati',
      body: `Velox Sàrl — Contatto protezione dati
38, rue Wilson, L-2732 Lussemburgo, Granducato di Lussemburgo
Email: info@myvelox.eu`,
    },
    {
      title: '4. A chi si applica',
      body: `Trattiamo dati di visitatori del sito, trasportatori e relativi rappresentanti e autisti, rappresentanti dei mittenti, nonché personale e collaboratori di Velox.`,
    },
    {
      title: '5. Dati personali raccolti',
      body: `5.1 Visitatori del sito. Indirizzo IP, informazioni su browser e dispositivo, pagine visitate e dati di utilizzo analoghi, anche tramite cookie (cfr. sezione 8).

5.2 Registrazione e verifica dei trasportatori: dati identificativi e di contatto; documenti d'identità e prova della capacità e dei poteri di rappresentanza; dati regolamentari e di conformità (licenze, autorizzazioni, certificati assicurativi, screening sanzioni); dati finanziari (coordinate bancarie); dati di flotta (targa, tipo, capacità, allestimenti).

5.3 Utilizzo del portale: contenuti del trasportatore (prove di consegna, certificati assicurativi e altri documenti); dati GPS dei veicoli; dati di performance (punteggio e metriche); dati di account e log di accesso.

5.4 Dati degli autisti. Quando gli autisti sono identificabili nei contenuti o nei dati GPS, trattiamo tali dati come descritto. I trasportatori devono informare i propri autisti e raccogliere, ove necessario, i consensi richiesti.

5.5 Dati dei rappresentanti dei mittenti. Nome, recapiti professionali e ruolo.

5.6 Corrispondenza. Se ci contattate, raccogliamo le informazioni che ci fornite.

5.7 Personale e collaboratori. Dati identificativi e di contatto e informazioni relative al rapporto di lavoro (retribuzioni, risorse umane, previdenza e fisco).`,
    },
    {
      title: '6. Finalità e basi giuridiche',
      body: `• Registrazione e verifica dei trasportatori, incluso lo screening sanzioni — esecuzione del contratto (art. 6(1)(b)) e obbligo legale (art. 6(1)(c)).
• Gestione del portale e dell'app e dei contratti di trasporto — art. 6(1)(b).
• Visibilità delle spedizioni, gestione dei trasporti e ottimizzazione dei percorsi — art. 6(1)(b) e legittimo interesse (art. 6(1)(f)).
• Calcolo e utilizzo del punteggio del trasportatore — legittimo interesse (art. 6(1)(f)).
• Fatturazione, gestione dei pagamenti e contabilità — art. 6(1)(b) e 6(1)(c).
• Prevenzione delle frodi e monitoraggio delle violazioni — legittimo interesse (art. 6(1)(f)).
• Conformità a sanzioni, antiriciclaggio e altri obblighi — art. 6(1)(c).
• Risposta a comunicazioni e richieste di assistenza — art. 6(1)(b) o 6(1)(f).
• Marketing diretto — vostro consenso (art. 6(1)(a)), ove richiesto.
• Cookie e analisi del sito — vostro consenso (art. 6(1)(a)), salvo cookie strettamente necessari (art. 6(1)(f)).
• Gestione dei rapporti di lavoro e di collaborazione — art. 6(1)(b) e 6(1)(c).

Qualora trattiamo eccezionalmente categorie particolari di dati (art. 9 GDPR), ci basiamo sulle deroghe applicabili, come l'accertamento, l'esercizio o la difesa di un diritto in sede giudiziaria o un motivo di interesse pubblico rilevante.`,
    },
    {
      title: '7. Processo decisionale automatizzato e profilazione',
      body: `7.1 Calcoliamo un punteggio per ciascun trasportatore sulla base di metriche di qualità e affidabilità (copertura GPS, puntualità, tasso di accettazione, criticità). Ciò costituisce una forma di profilazione.

7.2 Il punteggio può incidere sulle opzioni di prenotazione disponibili. Non lo utilizziamo per decisioni con effetti giuridici o similmente significativi basate esclusivamente su trattamenti automatizzati senza possibilità di riesame umano. In caso di errore potete chiedere un riesame.`,
    },
    {
      title: '8. Cookie e tecnologie analoghe',
      body: `8.1 Il sito e il portale utilizzano cookie strettamente necessari (sicurezza e gestione della sessione, con Cloudflare e la nostra piattaforma di hosting) e, con il vostro consenso, cookie analitici (attualmente PostHog).

8.2 Ove richiesto dalla legge, chiediamo il consenso prima di installare cookie non essenziali e mettiamo a disposizione uno strumento per accettare, rifiutare o modificare le preferenze in qualsiasi momento.

8.3 I dettagli sono riportati nella nostra politica sui cookie.`,
    },
    {
      title: '9. Destinatari dei dati',
      body: `(a) trasportatori e mittenti, nella misura necessaria all'esecuzione di un contratto di trasporto;
(b) fornitori di servizi che agiscono per nostro conto (infrastruttura cloud e hosting, pagamenti e fatturazione, comunicazione, sicurezza e analisi; attualmente Cloudflare e PostHog);
(c) partner finanziari e assicurativi, inclusi partner di factoring;
(d) consulenti professionali (revisori, avvocati, commercialisti);
(e) autorità pubbliche, ove previsto dalla legge; e
(f) un'entità subentrante, in caso di fusione, acquisizione o cessione.

Non vendiamo dati personali a terzi.`,
    },
    {
      title: '10. Trasferimenti internazionali',
      body: `10.1 Trattiamo i dati principalmente nell'Unione europea e nello Spazio economico europeo («SEE»). Con gli attuali fornitori e infrastrutture, i dati raccolti tramite sito e portale sono attualmente trattati nel SEE (in Germania).

10.2 In caso di trasferimenti fuori dal SEE, garantiamo un meccanismo giuridico adeguato: decisione di adeguatezza, clausole contrattuali tipo o altra garanzia riconosciuta dal GDPR.`,
    },
    {
      title: '11. Conservazione dei dati',
      body: `11.1 Conserviamo i dati solo per il tempo necessario, tenendo conto della durata del rapporto contrattuale, dei termini di legge (attualmente dieci (10) anni per le scritture contabili e le fatture) e del legittimo interesse alla difesa in giudizio.

11.2 In generale:
• Documentazione di registrazione e verifica e contratti di trasporto — durata del rapporto più cinque (5) anni.
• Dati GPS e contenuti del trasportatore — al massimo cinque (5) anni.
• Punteggio e metriche di performance — periodo di calcolo più uno storico limitato, massimo cinque (5) anni.
• Documenti finanziari e di fatturazione — dieci (10) anni.
• Documenti del personale e dei collaboratori — durata del rapporto più almeno cinque (5) anni.
• Dati di analisi e cookie — come indicato nella politica sui cookie.`,
    },
    {
      title: '12. Sicurezza dei dati',
      body: `12.1 Adottiamo misure tecniche e organizzative adeguate: sistemi protetti da password e, ove opportuno, cifrati; archiviazione e gestione documentale sicure; controlli di accesso secondo il principio della necessità di conoscere; e formazione del personale.

12.2 In caso di violazione dei dati personali che possa comportare un rischio per i diritti e le libertà delle persone, notificheremo la CNPD entro settantadue (72) ore e informeremo, se necessario, gli interessati senza ingiustificato ritardo.`,
    },
    {
      title: '13. I vostri diritti',
      body: `13.1 Nei limiti previsti dal GDPR avete diritto a: (a) accedere ai dati; (b) chiederne la rettifica; (c) chiederne la cancellazione; (d) chiedere la limitazione del trattamento; (e) opporvi ai trattamenti basati sul legittimo interesse, inclusa la profilazione, e al marketing diretto; (f) chiedere la portabilità; e (g) revocare il consenso in qualsiasi momento.

13.2 Per esercitarli, contattateci ai recapiti della sezione 3. Potremmo dover verificare la vostra identità. Risponderemo entro un (1) mese, prorogabile di due (2) mesi.

13.3 Alcuni diritti possono essere limitati da obblighi di legge o dalla necessità di accertare o difendere diritti in giudizio.

13.4 Avete inoltre diritto di proporre reclamo a un'autorità di controllo. In Lussemburgo:
Commission nationale pour la protection des données (CNPD)
15, Boulevard du Jazz, L-4370 Belvaux — Tel.: +352 26 10 60 1 — info@cnpd.lu — www.cnpd.public.lu`,
    },
    {
      title: '14. Minori',
      body: `I nostri servizi sono destinati esclusivamente a imprese e professionisti. Minori e consumatori non possono registrarsi sul portale e non raccogliamo consapevolmente dati di minori.`,
    },
    {
      title: '15. Modifiche alla presente informativa',
      body: `Riesaminiamo l'informativa almeno una volta all'anno e la aggiorniamo quando necessario per riflettere modifiche dei servizi, delle attività o dei sistemi, o della normativa applicabile. La versione aggiornata sarà pubblicata con la relativa data di efficacia.`,
    },
    {
      title: '16. Contatti',
      body: `Velox Sàrl
38, rue Wilson, L-2732 Lussemburgo, Granducato di Lussemburgo
Email: info@myvelox.eu`,
    },
  ],
};

const CONTENT: Record<string, PrivacyNoticeContent> = { en, fr, es, de, it };

export const getPrivacyNotice = (language: string): PrivacyNoticeContent =>
  CONTENT[(language || 'fr').split('-')[0]] ?? fr;
